import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import Card from "../components/Card";
import Input from "../components/Input";
import Button from "../components/Button";
import {
  listCookieAccounts,
  addCookieAccountAuto,
  deleteCookieAccount,
  upsertCookie,
  deleteCookie,
  cleanupCookies,
} from "../api/cookies.js";

const DEFAULT_COOKIE_NAMES = ["cf_clearance", "cookie_preferences_set_v", "KP_UIDz", "KP_UIDz-ssn", "session_token", "USER_LOCALE"];
const DEFAULT_DOMAIN = ".ugordi.com";
const DEFAULT_PATH = "/";

function emptyValues() {
  return DEFAULT_COOKIE_NAMES.reduce((acc, k) => ({ ...acc, [k]: "" }), {});
}

export default function CookiesPage({ onNavigate }) {
  const [accounts, setAccounts] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // yeni account eklerken sadece value girilecek
  const [values, setValues] = useState(() => emptyValues());

  async function refresh() {
    const data = await listCookieAccounts();
    setAccounts(data.accounts || []);
  }

  useEffect(() => {
    refresh().catch(() => {});
  }, []);

  async function onAddAccount() {
    setErr("");
    setBusy(true);
    try {
      const r = await addCookieAccountAuto(values);
      setAccounts(r.accounts || []);
      setValues(emptyValues());
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function onCleanup() {
    setErr("");
    setBusy(true);
    try {
      const r = await cleanupCookies();
      setAccounts(r.accounts || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Layout
      title="Cookie Yönetimi"
      right={
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={() => onNavigate?.("dashboard")}>
            Dashboard'a Dön
          </Button>
          <Button onClick={onCleanup} disabled={busy}>
            Temizle / Düzelt
          </Button>
        </div>
      }
    >
      <div className="grid">
        {/* Account Ekle */}
        <Card>
          <div className="h2">Account Ekle (Otomatik)</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Domain: <b>{DEFAULT_DOMAIN}</b> | Path: <b>{DEFAULT_PATH}</b> | Sadece value gir.
          </div>

          <div className="stack" style={{ marginTop: 10 }}>
            {DEFAULT_COOKIE_NAMES.map((name) => (
              <Input
                key={name}
                label={`${name} value`}
                value={values[name]}
                onChange={(e) => setValues((s) => ({ ...s, [name]: e.target.value }))}
              />
            ))}

            {err ? <div className="error">{err}</div> : null}

            <Button onClick={onAddAccount} disabled={busy}>
              Ekle
            </Button>
          </div>
        </Card>

        {/* Account Listesi */}
        <Card>
          <div className="h2">Account Listesi</div>
          <div className="list">
            {accounts.map((a) => (
              <AccountRow
                key={a.account}
                acc={a}
                onChanged={(next) => setAccounts(next)}
                setErr={setErr}
              />
            ))}
            {accounts.length === 0 ? <div className="muted">Account yok.</div> : null}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

function AccountRow({ acc, onChanged, setErr }) {
  const [busy, setBusy] = useState(false);

  // hesapta cookie name->cookie map (kolay erişim)
  const cookieMap = useMemo(() => {
    const map = new Map();
    (acc.cookies || []).forEach((c) => map.set(c.name, c));
    return map;
  }, [acc.cookies]);

  // her zaman 6 tane satır gözüksün, yoksa boş göster
  const rows = useMemo(() => {
    return DEFAULT_COOKIE_NAMES.map((name) => {
      const c = cookieMap.get(name);
      return (
        c || {
          name,
          value: "",
          domain: DEFAULT_DOMAIN,
          path: DEFAULT_PATH,
        }
      );
    });
  }, [cookieMap]);

  async function onDelAccount() {
    setErr("");
    setBusy(true);
    try {
      const r = await deleteCookieAccount(acc.account);
      onChanged(r.accounts || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row" style={{ alignItems: "flex-start" }}>
      <div style={{ flex: 1 }}>
        <div className="mono">account: {acc.account}</div>

        <div className="stack" style={{ marginTop: 10 }}>
          {rows.map((c) => (
            <CookieRow
              key={c.name}
              account={acc.account}
              cookie={c}
              onChanged={onChanged}
              setErr={setErr}
            />
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="ghost" onClick={onDelAccount} disabled={busy}>
          Account Sil
        </Button>
      </div>
    </div>
  );
}

function CookieRow({ account, cookie, onChanged, setErr }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(cookie);
  const [busy, setBusy] = useState(false);

  // cookie prop güncellenirse form da güncellensin (liste refresh olunca)
  useEffect(() => {
    setForm(cookie);
  }, [cookie]);

  async function onSave() {
    setErr("");
    setBusy(true);
    try {
      // domain/path sabit kalsın
      const payload = {
        name: cookie.name,
        value: form.value || "",
        domain: DEFAULT_DOMAIN,
        path: DEFAULT_PATH,
      };

      const r = await upsertCookie(account, payload);
      onChanged(r.accounts || []);
      setEdit(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    setErr("");
    setBusy(true);
    try {
      const r = await deleteCookie(account, cookie.name);
      onChanged(r.accounts || []);
      setEdit(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!edit) {
    const preview =
      cookie.value && cookie.value.length > 70
        ? cookie.value.slice(0, 70) + "..."
        : cookie.value || "(boş)";

    return (
      <div className="row">
        <div style={{ flex: 1 }}>
          <div>
            <b>{cookie.name}</b>
          </div>
          <div className="muted">
            domain: {DEFAULT_DOMAIN} | path: {DEFAULT_PATH}
          </div>
          <div className="mono" style={{ fontSize: 12, opacity: 0.8, wordBreak: "break-all" }}>
            {preview}
          </div>
        </div>

        <Button variant="secondary" onClick={() => setEdit(true)}>
          Düzenle
        </Button>
        <Button variant="ghost" onClick={onDelete} disabled={busy}>
          Sil
        </Button>
      </div>
    );
  }

  return (
    <div
      className="stack"
      style={{
        padding: 10,
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 10,
      }}
    >
      <div className="muted" style={{ fontSize: 12 }}>
        <b>{cookie.name}</b> (domain/path sabit)
      </div>

      <Input
        label="value"
        value={form.value || ""}
        onChange={(e) => setForm((s) => ({ ...s, value: e.target.value }))}
      />

      <div style={{ display: "flex", gap: 8 }}>
        <Button onClick={onSave} disabled={busy}>
          Kaydet
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setForm(cookie);
            setEdit(false);
          }}
          disabled={busy}
        >
          Vazgeç
        </Button>
      </div>

      <div className="muted" style={{ fontSize: 12 }}>
        Not: value boş kaydedersen backend bunu silme gibi yorumlayabilir (senin PUT endpointin öyle).
      </div>
    </div>
  );
}
